/**
 * Icon Registry for Attack Path Nodes
 *
 * Centralized registry of all icons available for asset nodes.
 * Single source of truth used by both AssetNode (rendering) and
 * AssetIconStep (selection wizard).
 *
 * @example
 * ```tsx
 * // Get icon component for rendering
 * const Icon = getIconById('Server');
 * return <Icon className="size-8" />;
 *
 * // Check if icon exists
 * if (hasIcon('Server')) { ... }
 *
 * // Get label for display
 * const label = getIconLabel('Server'); // 'Server'
 * ```
 */

import {
  Archive,
  Award,
  Bot,
  Briefcase,
  Cloud,
  Container,
  Cpu,
  Gem,
  Hash,
  KeyRound,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Shield,
  User,
  Users,
  Zap,
} from 'lucide-react';
import type React from 'react';

import { DefaultAssetIcon } from '@/sections/attacks/attackPaths/components/icons/bundles/DefaultAssetIcon';
// Infrastructure icons with AD Site color (#8B5CF6)
import InfrastructureComputer from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-computer.svg?react';
import InfrastructureDatabase from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-database.svg?react';
import InfrastructureFirewall from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-firewall.svg?react';
import InfrastructureNetwork from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-network.svg?react';
import InfrastructureRouter from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-router.svg?react';
import InfrastructureServer from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-server.svg?react';
import InfrastructureWebApplication from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-web-application.svg?react';
import { unscopeIconId } from '@/sections/attacks/attackPaths/components/icons/utils/iconSelectionUtils';

/**
 * Icon definition for asset node customization
 */
export interface IconDefinition {
  /** Unique identifier for the icon (empty string = default) */
  id: string;
  /** React component to render */
  component: React.ComponentType<{ className?: string; size?: number }>;
  /** Human-readable label for UI display */
  label: string;
  /** Category for organizational grouping */
  category:
    | 'infrastructure'
    | 'cloud'
    | 'identity'
    | 'applications'
    | 'security'
    | 'default';
}

/**
 * Complete registry of available icons for asset nodes.
 * Order determines display order in selection UI.
 */
export const iconRegistry: IconDefinition[] = [
  // === INFRASTRUCTURE (using custom SVGs with AD Site color #8B5CF6) ===
  {
    id: 'Computer',
    component: InfrastructureComputer as React.ComponentType<{
      className?: string;
      size?: number;
    }>,
    label: 'Computer/Endpoint',
    category: 'infrastructure',
  },
  {
    id: 'Server',
    component: InfrastructureServer as React.ComponentType<{
      className?: string;
      size?: number;
    }>,
    label: 'Server',
    category: 'infrastructure',
  },
  {
    id: 'Network',
    component: InfrastructureNetwork as React.ComponentType<{
      className?: string;
      size?: number;
    }>,
    label: 'Network/Subnet',
    category: 'infrastructure',
  },
  {
    id: 'Router',
    component: InfrastructureRouter as React.ComponentType<{
      className?: string;
      size?: number;
    }>,
    label: 'Router/Gateway',
    category: 'infrastructure',
  },
  {
    id: 'Database',
    component: InfrastructureDatabase as React.ComponentType<{
      className?: string;
      size?: number;
    }>,
    label: 'Database/RDS',
    category: 'infrastructure',
  },

  // === CLOUD ===
  { id: 'Cloud', component: Cloud, label: 'Cloud Account', category: 'cloud' },
  {
    id: 'Compute',
    component: Cpu,
    label: 'VM/EC2 Instance',
    category: 'cloud',
  },
  {
    id: 'Serverless',
    component: Zap,
    label: 'Lambda/Function',
    category: 'cloud',
  },
  {
    id: 'Storage',
    component: Archive,
    label: 'S3/Blob Storage',
    category: 'cloud',
  },
  {
    id: 'Container',
    component: Container,
    label: 'Container/ECR',
    category: 'cloud',
  },

  // === IDENTITY ===
  { id: 'User', component: User, label: 'User', category: 'identity' },
  { id: 'Group', component: Users, label: 'Group', category: 'identity' },
  {
    id: 'Principal',
    component: KeyRound,
    label: 'IAM Role/Principal',
    category: 'identity',
  },
  {
    id: 'CertAuthority',
    component: Award,
    label: 'Certificate Authority',
    category: 'identity',
  },

  // === APPLICATIONS ===
  {
    id: 'WebApp',
    component: InfrastructureWebApplication as React.ComponentType<{
      className?: string;
      size?: number;
    }>,
    label: 'Web Application',
    category: 'applications',
  },
  {
    id: 'Bot',
    component: Bot,
    label: 'Bot/Automation/LLM',
    category: 'applications',
  },

  // === SOCIAL ENGINEERING BADGES ===
  { id: 'Mail', component: Mail, label: 'Email', category: 'applications' },
  {
    id: 'MessageSquare',
    component: MessageSquare,
    label: 'SMS/Message',
    category: 'applications',
  },
  {
    id: 'Phone',
    component: Phone,
    label: 'Phone Call',
    category: 'applications',
  },
  { id: 'Shield', component: Shield, label: '2FA/MFA', category: 'security' },
  {
    id: 'Hash',
    component: Hash,
    label: 'Slack/Channel',
    category: 'applications',
  },
  {
    id: 'Briefcase',
    component: Briefcase,
    label: 'LinkedIn/Professional',
    category: 'applications',
  },
  {
    id: 'Users',
    component: Users,
    label: 'Facebook/Social',
    category: 'applications',
  },
  {
    id: 'MessageCircle',
    component: MessageCircle,
    label: 'Twitter/Social',
    category: 'applications',
  },

  // === SECURITY & TARGETS ===
  {
    id: 'Firewall',
    component: InfrastructureFirewall as React.ComponentType<{
      className?: string;
      size?: number;
    }>,
    label: 'Firewall/Security',
    category: 'security',
  },
  {
    id: 'CrownJewel',
    component: Gem,
    label: 'Critical Asset',
    category: 'security',
  },

  // === DEFAULT ===
  {
    id: '',
    component: DefaultAssetIcon,
    label: 'Default (Clear)',
    category: 'default',
  },
];

/**
 * Map for O(1) icon lookups by id.
 * Includes both regular icons and the default (empty string) icon.
 */
const iconMap = new Map<string, IconDefinition>(
  iconRegistry.map(def => [def.id, def])
);

/**
 * Default icon definition used when no override is specified
 * or when an invalid icon id is provided.
 */
const defaultIcon = iconRegistry.find(def => def.id === '')!;

/**
 * Get icon component by id.
 *
 * @param iconId - Icon identifier (or undefined/empty for default)
 * @returns React component for the icon
 *
 * @example
 * ```tsx
 * const Icon = getIconById('Server');
 * return <Icon className="text-chariot-text-primary size-8" />;
 * ```
 */
export function getIconById(
  iconId?: string
): React.ComponentType<{ className?: string; size?: number }> {
  const unscopedId = unscopeIconId(iconId);
  const definition = iconMap.get(unscopedId) ?? defaultIcon;
  return definition.component;
}

/**
 * Get full icon definition by id.
 *
 * @param iconId - Icon identifier (or undefined/empty for default)
 * @returns Full IconDefinition object
 */
export function getIconDefinition(iconId?: string): IconDefinition {
  const unscopedId = unscopeIconId(iconId);
  return iconMap.get(unscopedId) ?? defaultIcon;
}

/**
 * Get human-readable label for an icon.
 *
 * @param iconId - Icon identifier (or undefined/empty for default)
 * @returns Human-readable label
 */
export function getIconLabel(iconId?: string): string {
  const unscopedId = unscopeIconId(iconId);
  const definition = iconMap.get(unscopedId) ?? defaultIcon;
  return definition.label;
}

/**
 * Check if an icon id exists in the registry.
 *
 * @param iconId - Icon identifier to check
 * @returns true if icon exists, false otherwise
 */
export function hasIcon(iconId: string): boolean {
  const unscopedId = unscopeIconId(iconId);
  return iconMap.has(unscopedId);
}
